import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const F = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-xs font-medium">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const bind = (form, set, k) => ({ value: form[k] || "", onChange: (e) => set(k, e.target.value), "data-testid": `setup-${k}` });

export const CompanyStep = ({ form, set, tokenRequired, onLogo, logoPreview }) => (
  <div className="space-y-4">
    {tokenRequired && <F label="Setup token" hint="Provided by whoever deployed this system."><Input type="password" {...bind(form, set, "setup_token")} /></F>}
    <F label="Company name *"><Input placeholder="Acme Training Ltd" {...bind(form, set, "company_name")} /></F>
    <div className="grid grid-cols-2 gap-3">
      <F label="Short name" hint="Shown on the workspace switch"><Input placeholder="ACME" {...bind(form, set, "short_name")} /></F>
      <F label="Website"><Input placeholder="acmetraining.co.uk" {...bind(form, set, "website")} /></F>
    </div>
    <F label="Public contact email"><Input type="email" placeholder="hello@acmetraining.co.uk" {...bind(form, set, "company_email")} /></F>
    <div className="flex flex-wrap items-end gap-4">
      <F label="Brand colour">
        <input type="color" value={form.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-16 cursor-pointer rounded border border-border" data-testid="setup-brand_color" />
      </F>
      <F label="Logo (PNG, JPG or WebP, under 2 MB)">
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="text-xs" data-testid="setup-logo-input" />
      </F>
      {logoPreview && <img src={logoPreview} alt="Logo preview" className="h-10 object-contain" data-testid="setup-logo-preview" />}
    </div>
  </div>
);

export const LmsStep = ({ form, set }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">Your LMS and assessment portal (courses, quizzes, AI-writing detection and tutor marking) runs as a separate workspace. Learner data is never shared with the CRM.</p>
    <F label="LMS / Assessment portal name *" hint="Learners see this on their portal and in emails."><Input placeholder="Acme Academy" {...bind(form, set, "lms_name")} /></F>
    <F label="Short name" hint="Shown on the workspace switch, e.g. 'Academy'"><Input placeholder="Academy" {...bind(form, set, "lms_short_name")} /></F>
  </div>
);

export const AdminStep = ({ form, set }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">This becomes the first superadmin. You can invite the rest of your team afterwards.</p>
    <F label="Full name *"><Input {...bind(form, set, "admin_name")} /></F>
    <F label="Email *"><Input type="email" {...bind(form, set, "admin_email")} /></F>
    <div className="grid grid-cols-2 gap-3">
      <F label="Password *" hint="At least 8 characters with upper, lower case and a number"><Input type="password" {...bind(form, set, "admin_password")} /></F>
      <F label="Confirm password *"><Input type="password" {...bind(form, set, "admin_password2")} /></F>
    </div>
  </div>
);

export const EmailStep = ({ form, set }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">Invites, reminders and receipts are sent from this address. It must be a verified sender signature in your Postmark account.</p>
    <F label="Sender email"><Input type="email" placeholder="noreply@acmetraining.co.uk" {...bind(form, set, "sender_email")} /></F>
    <F label="Sender name"><Input placeholder="Acme Training" {...bind(form, set, "sender_name")} /></F>
  </div>
);

export const PacksStep = ({ form, setPacks, packs }) => (
  <div className="space-y-3">
    <p className="text-sm text-muted-foreground">Optionally start with a ready-made UK qualification catalogue. You can edit or add your own at any time.</p>
    {packs.map((p) => (
      <label key={p.key} className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
        <input type="checkbox" checked={form.packs.includes(p.key)} onChange={() => setPacks(form.packs.includes(p.key) ? form.packs.filter((x) => x !== p.key) : [...form.packs, p.key])} data-testid={`setup-pack-${p.key}`} />
        <span className="flex-1">{p.label}</span><span className="text-xs text-muted-foreground">{p.count} qualifications</span>
      </label>
    ))}
    <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setPacks([])} data-testid="setup-pack-none">Start with none</button>
  </div>
);

export const ReviewStep = ({ form, packs }) => (
  <dl className="grid grid-cols-[150px_1fr] gap-y-2 text-sm" data-testid="setup-review">
    {[
      ["Company", form.company_name], ["Short name", form.short_name || "—"], ["Brand colour", form.brand_color],
      ["LMS name", form.lms_name], ["Admin", `${form.admin_name} · ${form.admin_email}`],
      ["Email sender", form.sender_email || "Not set (configure later)"],
      ["Qualification packs", form.packs.length ? packs.filter((p) => form.packs.includes(p.key)).map((p) => p.label).join(", ") : "None"],
    ].flatMap(([k, v]) => [<dt key={`${k}-k`} className="text-muted-foreground">{k}</dt>, <dd key={`${k}-v`} className="font-medium">{v}</dd>])}
  </dl>
);
